/**
=========================================================
* Trakib - v2.1.0 
=========================================================

* Product Page: https://www.trakib.com/product/material-dashboard-pro-react
* Copyright 2022 Trakib (https://www.trakib.com)

Coded by www.trakib.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect, useState } from "react";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import { InputLabel, Autocomplete, Checkbox } from "@mui/material";

// Trakib components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";

// Trakib examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import FormField from "components/FormField";
import { useNavigate, useParams } from "react-router-dom";

import CrudService from "services/cruds-service";
import i18n from "i18n";

const EditDonation = () => {
  const { id } = useParams();
  const { t } = i18n;
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [donation, setDonation] = useState({
    user_id: null,
    project_id: null,
    OrderId: "",
    Currency: "",
    PurchAmount: "",
    TransactionDate: "",
    Description: "",
  });

  const [error, setError] = useState({
    user_id: false,
    project_id: false,
    OrderId: false,
    Currency: false,
    PurchAmount: false,
    TransactionDate: false,
    Description: false,
    error: false,
    textError: "",
  });

  useEffect(() => {
    (async () => {
      try {
        const usersRes = await CrudService.getAllData(
          "items/projects?fields=id,translations.name"
        );
        setProjects(usersRes.data);
        const userRes = await CrudService.getAllData(
          `users?fields=id,first_name`
        );
        setUsers(userRes.data);
      } catch (err) {
        console.error(err);
        return null;
      }
    })();
  }, []);

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const response = await CrudService.getAllData(
          `items/donation/${id}?fields=*,project_id.id,project_id.translations.name,user_id.first_name,user_id.id`
        );
        setDonation(response.data);
      } catch (err) {
        console.error(err);
      }
    })();
  }, [id]);

  const submitHandler = async (e) => {
    e.preventDefault();

    try {
      const newDonation = {
        project_id: donation.project_id?.id,
        OrderId: donation.OrderId,
        Currency: donation.Currency,
        PurchAmount: donation.PurchAmount,
        TransactionDate: donation.TransactionDate,
        Description: donation.Description,
        user_id: donation.user_id?.id,
        from: "org",
      };

      try {
        await CrudService.updateItem(
          `items/donation/${donation.id}`,
          newDonation
        );
        navigate("/donationManagement/donations", {
          state: { value: true, text: t("updateSuccessfully") },
        });
      } catch (err) {
        if (err.hasOwnProperty("errors")) {
          setError({ ...error, error: true, textError: err.errors[0].detail });
        }
        console.error(err);
      }
    } catch (err) {
      setError({ ...error, error: true, textError: err.message });
      return null;
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar breadcrumbTitle={`${donation.id}`} />
      <MDBox mt={5} mb={9}>
        <Grid container justifyContent="center">
          <Grid item xs={12} lg={8}>
            <MDBox mt={6} mb={8} textAlign="center">
              <MDBox mb={1}>
                <MDTypography variant="h3" fontWeight="bold">
                  {t("updateDonation")}
                </MDTypography>
              </MDBox>
            </MDBox>
            <Card>
              <MDBox
                component="form"
                method="POST"
                onSubmit={submitHandler}
                encType="multipart/form-data"
              >
                <MDBox display="flex" flexDirection="column" px={3} my={4}>
                  <MDBox display="flex" flexDirection="column" marginTop="2rem">
                    <Autocomplete
                      defaultValue={null}
                      options={projects}
                      getOptionLabel={(option) => {
                        console.log(option);

                        if (option.data) {
                          if (option.data.translations[0].name)
                            return option.data.translations[0].name;
                        } else {
                          if (option) {
                            if (option.translations[0].name)
                              return option.translations[0].name;
                          }
                        }
                        return "";
                      }}
                      value={donation.project_id}
                      isOptionEqualToValue={(option, item) =>
                        option === item || option.id === item.id
                      }
                      onChange={(event, newValue) => {
                        setDonation({
                          ...donation,
                          project_id: newValue,
                        });
                      }}
                      renderInput={(params) => (
                        <FormField
                          {...params}
                          label={t("projects")}
                          InputLabelProps={{ shrink: true }}
                        />
                      )}
                    />
                    {error.project_id && (
                      <MDTypography
                        variant="caption"
                        color="error"
                        fontWeight="light"
                        pl={4}
                      >
                        {error.textError}
                      </MDTypography>
                    )}
                  </MDBox>
                  <MDBox display="flex" flexDirection="column" marginTop="2rem">
                    <Autocomplete
                      defaultValue={null}
                      options={users}
                      getOptionLabel={(option) => {
                        if (option.data) {
                          if (option.data.first_name)
                            return option.data.first_name;
                        } else {
                          if (option) {
                            if (option.first_name) return option.first_name;
                          }
                        }
                        return "";
                      }}
                      value={donation.user_id}
                      isOptionEqualToValue={(option, item) =>
                        option === item || option.id === item.id
                      }
                      onChange={(event, newValue) => {
                        setDonation({
                          ...donation,
                          user_id: newValue,
                        });
                      }}
                      renderInput={(params) => (
                        <FormField
                          {...params}
                          label={t("users")}
                          InputLabelProps={{ shrink: true }}
                        />
                      )}
                    />
                    {error.user_id && (
                      <MDTypography
                        variant="caption"
                        color="error"
                        fontWeight="light"
                        pl={4}
                      >
                        {error.textError}
                      </MDTypography>
                    )}
                  </MDBox>
                  <MDBox display="flex" flexDirection="column" marginTop="2rem">
                    <FormField
                      variant="outlined"
                      label={t("OrderId")}
                      placeholder={t("OrderId")}
                      name="OrderId"
                      value={donation.OrderId}
                      onChange={(e) => {
                        setDonation({
                          ...donation,
                          OrderId: e.target.value,
                        });
                      }}
                    />
                  </MDBox>
                  <MDBox display="flex" flexDirection="column" marginTop="2rem">
                    <FormField
                      variant="outlined"
                      label={t("Currency")}
                      placeholder={t("Currency")}
                      name="Currency"
                      value={donation.Currency}
                      onChange={(e) => {
                        setDonation({
                          ...donation,
                          Currency: e.target.value,
                        });
                      }}
                    />
                  </MDBox>
                  <MDBox display="flex" flexDirection="column" marginTop="2rem">
                    <FormField
                      variant="outlined"
                      label={t("PurchAmount")}
                      placeholder={t("PurchAmount")}
                      name="PurchAmount"
                      value={donation.PurchAmount}
                      onChange={(e) => {
                        setDonation({
                          ...donation,
                          PurchAmount: e.target.value,
                        });
                      }}
                    />
                  </MDBox>
                  <MDBox display="flex" flexDirection="column" marginTop="2rem">
                    <FormField
                      variant="outlined"
                      label={t("date")}
                      placeholder={t("TransactionDate")}
                      name="TransactionDate"
                      value={donation.TransactionDate}
                      onChange={(e) => {
                        setDonation({
                          ...donation,
                          TransactionDate: e.target.value,
                        });
                      }}
                    />
                  </MDBox>
                  <MDBox display="flex" flexDirection="column" marginTop="2rem">
                    <FormField
                      multiline
                      variant="outlined"
                      rows={5}
                      label={t("details")}
                      placeholder={t("Description")}
                      name="Description"
                      value={donation.Description}
                      onChange={(e) => {
                        setDonation({
                          ...donation,
                          Description: e.target.value,
                        });
                      }}
                    />
                  </MDBox>
                  {error.error && (
                    <MDTypography
                      variant="caption"
                      color="error"
                      fontWeight="light"
                      pt={2}
                    >
                      {error.textError}
                    </MDTypography>
                  )}
                  <MDBox
                    ml="auto"
                    mt={4}
                    mb={2}
                    display="flex"
                    justifyContent="flex-end"
                  >
                    <MDBox mx={2}>
                      <MDButton
                        variant="gradient"
                        color="dark"
                        size="small"
                        px={2}
                        mx={2}
                        onClick={() =>
                          navigate("/donationManagement/donations-list", {
                            state: { value: false, text: "" },
                          })
                        }
                      >
                        {t("back")}
                      </MDButton>
                    </MDBox>
                    <MDButton
                      variant="gradient"
                      color="dark"
                      size="small"
                      type="submit"
                    >
                      {t("save")}
                    </MDButton>
                  </MDBox>
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
};

export default EditDonation;
